// The sign-in shared by every Altekore product in a region. On *.altekore.in (and *.altekore.com) the Supabase session
// lives in cookies for the whole parent domain, so Books, Payroll, Time and Accounts all see one sign-in: a new tab on
// any of them is already signed in, and signing out anywhere signs out everywhere. Anywhere else (localhost, previews)
// it stays in this browser's local storage, as before.
// The session is a few kB, more than one cookie holds, so it is split across numbered cookies (key.0, key.1 …).
// The same file is used by accounts/ (accounts/src/sharedSession.ts): keep the two identical.
import type { SupportedStorage } from "@supabase/supabase-js";

const CHUNK = 3000;
const MAX_CHUNKS = 8;
const YEAR = 400 * 24 * 3600;   // browsers cap cookie lifetime at 400 days; the session itself expires much sooner

/** The parent domain whose subdomains share the sign-in, or null to keep it local (localhost, *.pages.dev). */
export function sharedDomain(host: string): string | null {
  const m = host.toLowerCase().match(/(^|\.)(altekore\.(in|com))$/);
  return m ? m[2] : null;
}

function readCookies(src: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const part of src.split(";")) {
    const i = part.indexOf("=");
    if (i < 0) continue;
    const k = part.slice(0, i).trim();
    out.set(k, part.slice(i + 1).trim());   // still encoded: a chunk may end inside a multi-byte character
  }
  return out;
}

/** Splits a value into cookie-sized pieces (pure; tested). */
export function chunks(value: string): string[] {
  const enc = encodeURIComponent(value);
  const out: string[] = [];
  let i = 0;
  while (i < enc.length) {
    let end = Math.min(i + CHUNK, enc.length);
    // don't cut a %XX escape in two
    if (end < enc.length) { if (enc[end - 1] === "%") end -= 1; else if (enc[end - 2] === "%") end -= 2; }
    out.push(enc.slice(i, end));
    i = end;
  }
  return out;
}

export function cookieStorage(domain: string, doc: Document = document): SupportedStorage {
  const write = (name: string, value: string, maxAge: number) => {
    doc.cookie = `${name}=${value}; Domain=.${domain}; Path=/; Max-Age=${maxAge}; SameSite=Lax; Secure`;
  };
  const clear = (key: string) => { for (let n = 0; n < MAX_CHUNKS; n++) write(`${key}.${n}`, "", 0); };
  return {
    getItem(key) {
      const all = readCookies(doc.cookie);
      if (!all.has(`${key}.0`)) {
        // a session from before the shared sign-in, kept in this site's local storage: move it across once
        try {
          const old = localStorage.getItem(key);
          if (old) { this.setItem(key, old); localStorage.removeItem(key); return old; }
        } catch { /* storage blocked */ }
        return null;
      }
      let s = "";
      for (let n = 0; n < MAX_CHUNKS && all.has(`${key}.${n}`); n++) s += all.get(`${key}.${n}`);
      // decode once, after joining: decoding chunk by chunk breaks a character split across two cookies (é = %C3%A9)
      try { return decodeURIComponent(s) || null; } catch { return null; }
    },
    setItem(key, value) {
      clear(key);
      chunks(value).slice(0, MAX_CHUNKS).forEach((c, n) => write(`${key}.${n}`, c, YEAR));
    },
    removeItem(key) {
      clear(key);
      try { localStorage.removeItem(key); } catch { /* storage blocked */ }
    },
  };
}

/** Storage for the Supabase client: shared cookies on Altekore's domains, local storage elsewhere. */
export function sessionStorageFor(host: string): SupportedStorage | undefined {
  const d = sharedDomain(host);
  return d ? cookieStorage(d) : undefined;
}
